import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  Divider,
  Button,
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { API } from "aws-amplify";
import { useUser } from "../contexts/useUser";
import {
  getCustomerReplenishmentAmounts,
  getCustomerUsageTierName,
  getNextReplenishmentDate,
  getCustomerUsageLimitAction,
  getCustomerReplenishmentPeriod,
  getCustomerReplenishmentMethod,
} from "../utils/usageTierUtils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLLMUsageTiers } from "../dataaccess/fetchLLMUsageTiers";

interface CustomerUsageLimitsViewProps {
  selectedCustomer: any;
  navigate?: (path: string) => void;
  isAdmin?: boolean;
}

export const CustomerUsageLimitsView: React.FC<
  CustomerUsageLimitsViewProps
> = ({ selectedCustomer, navigate, isAdmin = false }) => {
  const { project } = useUser();
  const [isReplenishing, setIsReplenishing] = useState(false);
  const [localCustomer, setLocalCustomer] = useState(null);
  const [changeTierOpen, setChangeTierOpen] = useState(false);
  const [selectedNewTierId, setSelectedNewTierId] = useState<string>("");
  const [upgradeMethod, setUpgradeMethod] = useState<number>(4);
  const [isChangingTier, setIsChangingTier] = useState(false);
  const queryClient = useQueryClient();

  // Fetch usage tiers for selection (ACTIVE only in UI)
  const { data: tiersData } = useQuery({
    queryKey: ["usage-tiers", project?.id],
    queryFn: () => fetchLLMUsageTiers(project?.id ?? ""),
    enabled: Boolean(project?.id),
    staleTime: 5 * 60 * 1000,
  });
  const allTiers = tiersData?.data?.usageTiersByProjectId?.items ?? [];
  const activeTiers = allTiers.filter((t: any) => t?.status === "ACTIVE");
  const sortedActiveTiers = [...activeTiers].sort((a, b) =>
    `${a?.name ?? ""}`.localeCompare(`${b?.name ?? ""}`)
  );

  // Reset local customer when selected customer changes
  React.useEffect(() => {
    setLocalCustomer(null);
  }, [selectedCustomer?.id]);

  // Use local customer data if available, otherwise use prop
  const currentCustomer = localCustomer || selectedCustomer;
  const currentTierId = currentCustomer?.usageTierId ?? "";
  const isTierSelectionUnchanged = selectedNewTierId === currentTierId;

  if (!currentCustomer) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Select a customer to view usage and limits
        </Typography>
      </Box>
    );
  }

  // Get effective replenishment amounts from usage tier (simplified approach)
  const effectiveAmounts = getCustomerReplenishmentAmounts(currentCustomer);

  // Calculate next replenishment date using utility
  const getNextReplenishmentDateFormatted = () => {
    const nextDate = getNextReplenishmentDate(currentCustomer);
    return nextDate ? nextDate.toLocaleDateString() : "Not scheduled";
  };

  // Prepare token usage data for bar chart
  const tokenUsageData = [
    {
      category: "Standard",
      tierType: "standard",
      remaining: currentCustomer.standardTokensRemaining || 0,
      replenishment: effectiveAmounts.standardTokenReplenishmentAmount,
    },
    {
      category: "Premium",
      tierType: "premium",
      remaining: currentCustomer.premiumTokensRemaining || 0,
      replenishment: effectiveAmounts.premiumTokenReplenishmentAmount,
    },
    {
      category: "Super\nPremium",
      tierType: "superPremium",
      remaining: currentCustomer.superPremiumTokensRemaining || 0,
      replenishment: effectiveAmounts.superPremiumTokenReplenishmentAmount,
    },
    {
      category: "Reasoning",
      tierType: "reasoning",
      remaining: currentCustomer.reasoningTokensRemaining || 0,
      replenishment: effectiveAmounts.reasoningTokenReplenishmentAmount,
    },
  ];

  // Prepare call usage data for bar chart
  const callUsageData = [
    {
      category: "Standard",
      tierType: "standard",
      remaining: currentCustomer.standardCallsRemaining || 0,
      replenishment: effectiveAmounts.standardCallReplenishmentAmount,
    },
    {
      category: "Premium",
      tierType: "premium",
      remaining: currentCustomer.premiumCallsRemaining || 0,
      replenishment: effectiveAmounts.premiumCallReplenishmentAmount,
    },
    {
      category: "Super\nPremium",
      tierType: "superPremium",
      remaining: currentCustomer.superPremiumCallsRemaining || 0,
      replenishment: effectiveAmounts.superPremiumCallReplenishmentAmount,
    },
    {
      category: "Reasoning",
      tierType: "reasoning",
      remaining: currentCustomer.reasoningCallsRemaining || 0,
      replenishment: effectiveAmounts.reasoningCallReplenishmentAmount,
    },
  ];

  // Colors for different tiers (used in progress bars)
  const colors = ["#1976d2", "#2e7d32", "#ed6c02", "#d32f2f"];

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  const getUsagePercentage = (remaining: number, replenishment: number) => {
    if (replenishment === 0) return 0;
    const used = replenishment - remaining;
    return Math.max(0, Math.min(100, (used / replenishment) * 100));
  };

  const shouldShowPercentage = (
    remaining: number,
    replenishment: number,
    tierType: string,
    usageType: "token" | "call"
  ) => {
    // Don't show percentage for unlimited usage
    if (isUnlimited(tierType, usageType)) {
      return false;
    }
    // Don't show percentage if quota is 0 (whether remaining is positive or negative)
    return replenishment !== 0;
  };

  // Helper function to check if a specific tier/type has unlimited usage
  const isUnlimited = (tierType: string, usageType: "token" | "call") => {
    const key = `${tierType}${
      usageType === "token" ? "Token" : "Call"
    }Unlimited`;
    // Check customer-level unlimited flag first, then tier-level
    return currentCustomer[key] || currentCustomer.usageTier?.[key];
  };

  const getDisplayValue = (
    remaining: number,
    replenishment: number,
    tierType: string,
    usageType: "token" | "call"
  ) => {
    // Check if this tier/type combination is unlimited
    if (isUnlimited(tierType, usageType)) {
      return "Unlimited";
    }

    // If quota is 0, just show the absolute value of remaining (calls made when over limit)
    if (replenishment === 0) {
      return `${Math.abs(remaining)}`;
    }
    // Show calls/tokens made (allocation - remaining) / allocation format
    const used = replenishment - remaining;
    return `${formatNumber(used)} / ${formatNumber(replenishment)}`;
  };

  const handleReplenishNow = async () => {
    if (
      !selectedCustomer?.usageTierId ||
      !selectedCustomer?.id ||
      !project?.id
    ) {
      console.error("Missing required data for replenishment");
      return;
    }

    setIsReplenishing(true);

    try {
      await API.post("eventapi", "/replenishNow", {
        body: {
          projectId: project.id,
          customerId: selectedCustomer.id,
          usageTierId: selectedCustomer.usageTierId,
        },
      });

      // Create updated customer data with replenished amounts
      const effectiveAmounts =
        getCustomerReplenishmentAmounts(selectedCustomer);
      const updatedCustomer = {
        ...selectedCustomer,
        standardTokensRemaining:
          effectiveAmounts.standardTokenReplenishmentAmount,
        premiumTokensRemaining:
          effectiveAmounts.premiumTokenReplenishmentAmount,
        superPremiumTokensRemaining:
          effectiveAmounts.superPremiumTokenReplenishmentAmount,
        reasoningTokensRemaining:
          effectiveAmounts.reasoningTokenReplenishmentAmount,
        standardCallsRemaining:
          effectiveAmounts.standardCallReplenishmentAmount,
        premiumCallsRemaining: effectiveAmounts.premiumCallReplenishmentAmount,
        superPremiumCallsRemaining:
          effectiveAmounts.superPremiumCallReplenishmentAmount,
        reasoningCallsRemaining:
          effectiveAmounts.reasoningCallReplenishmentAmount,
        lastReplenishedAt: new Date().toISOString(),
      };

      // Update local state to show immediate changes
      setLocalCustomer(updatedCustomer);

      queryClient.invalidateQueries({
        queryKey: ["customers", { id: selectedCustomer.id }],
      });

      // Also invalidate broader customer queries to refresh parent component data
      queryClient.invalidateQueries({
        queryKey: ["customers"],
      });

      console.log("Customer replenished successfully");
    } catch (error) {
      console.error("Failed to replenish customer:", error);
      // You might want to show an error message to the user
    } finally {
      setIsReplenishing(false);
    }
  };

  const openChangeTierDialog = () => {
    // Preselect current tier if present
    setSelectedNewTierId(currentTierId);
    setUpgradeMethod(4); // Default to immediate reset
    setChangeTierOpen(true);
  };

  const handleConfirmChangeTier = async () => {
    if (!project?.id || !currentCustomer?.id || isTierSelectionUnchanged) {
      return;
    }
    setIsChangingTier(true);
    try {
      console.log(
        "Changing usage tier to:",
        selectedNewTierId,
        currentCustomer.usageTierId
      );

      await API.post("eventapi", "/changeUsageTier", {
        body: {
          projectId: project.id,
          customerId: currentCustomer.id,
          newUsageTierId: selectedNewTierId,
          upgradeMethod: upgradeMethod,
        },
      });

      // Update local state to reflect new tier immediately
      const newTier =
        allTiers.find((t: any) => t?.id === selectedNewTierId) || null;
      setLocalCustomer({
        ...(selectedCustomer as any),
        usageTierId: selectedNewTierId,
        usageTier: newTier,
      });

      // Invalidate relevant caches to force complete refresh
      queryClient.invalidateQueries({
        queryKey: ["customers", { id: currentCustomer.id }],
      });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      
      // Force a complete refresh by clearing local customer data
      // This will cause the component to refetch the updated data
      setLocalCustomer(null);
    } catch (e) {
      console.error("Failed to change usage tier", e);
    } finally {
      setIsChangingTier(false);
      setChangeTierOpen(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Usage & Limits - {currentCustomer.name}
        </Typography>
        {navigate && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`${currentCustomer.id}/usage-widget`)}
            sx={{ flexShrink: 0 }}
          >
            Usage Widget
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Usage Tier Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usage Tier
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Tier:
                </Typography>
                <Chip
                  label={
                    currentCustomer.usageTier
                      ? `${getCustomerUsageTierName(currentCustomer)}${
                          currentCustomer.usageTier.version
                            ? ` · v${currentCustomer.usageTier.version}`
                            : ""
                        }`
                      : getCustomerUsageTierName(currentCustomer)
                  }
                  color={currentCustomer.usageTier ? "primary" : "default"}
                  variant="outlined"
                  size="small"
                />
                {currentCustomer.usageTier && (
                  <>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      Action:
                    </Typography>
                    <Chip
                      label={
                        getCustomerUsageLimitAction(currentCustomer) ||
                        "Not set"
                      }
                      color={
                        getCustomerUsageLimitAction(currentCustomer) === "BLOCK"
                          ? "error"
                          : getCustomerUsageLimitAction(currentCustomer) ===
                            "ALLOW"
                          ? "success"
                          : "warning"
                      }
                      variant="outlined"
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      BLOCK: no calls allowed when limit hit • ALLOW: excess
                      calls tracked • RESTRICT: calls routed to standard tier
                    </Typography>
                  </>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={openChangeTierDialog}
                >
                  Change Tier
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Change Tier Dialog */}
        <Dialog
          open={changeTierOpen}
          onClose={() => setChangeTierOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Change Usage Tier</DialogTitle>
          <DialogContent>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel id="change-tier-select-label">
                Select Tier
              </InputLabel>
              <Select
                labelId="change-tier-select-label"
                label="Select Tier"
                displayEmpty
                value={selectedNewTierId}
                onChange={(e) => setSelectedNewTierId(e.target.value as string)}
              >
                <MenuItem value="" disabled={currentTierId === ""}>
                  No Usage Tier
                </MenuItem>
                {sortedActiveTiers.map((t: any) => (
                  <MenuItem
                    key={t?.id}
                    value={t?.id}
                    disabled={t?.id === currentCustomer?.usageTierId}
                  >
                    {t?.name}
                    {t?.version ? ` · v${t.version}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mt: 2 }} disabled={selectedNewTierId === ""}>
              <InputLabel id="upgrade-method-select-label">
                Upgrade Method
              </InputLabel>
              <Select
                labelId="upgrade-method-select-label"
                label="Upgrade Method"
                value={selectedNewTierId === "" ? 4 : upgradeMethod}
                onChange={(e) => setUpgradeMethod(e.target.value as number)}
              >
                <MenuItem value={1}>
                  <Box>
                    <Typography variant="body2" component="div">
                      Boundary switch (clean reset at next replenishment date)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Changes take effect at the next scheduled replenishment
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={2}>
                  <Box>
                    <Typography variant="body2" component="div">
                      Immediate delta add/subtract (no proration)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Adds/subtracts the difference between old and new tier
                      amounts immediately
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={3}>
                  <Box>
                    <Typography variant="body2" component="div">
                      Immediate prorated grant based on time remaining
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Grants prorated amounts based on time remaining until next
                      replenishment
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={4}>
                  <Box>
                    <Typography variant="body2" component="div">
                      Immediate reset to new tier (full allowance)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Resets balances to the new tier's full amounts immediately
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setChangeTierOpen(false)}
              disabled={isChangingTier}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmChangeTier}
              variant="contained"
              disabled={isChangingTier || isTierSelectionUnchanged}
            >
              {isChangingTier ? "Changing..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Detailed Usage Breakdown - Only show when there are limits */}
        {/* Always show usage breakdown if customer has a usage tier */}
        {currentCustomer.usageTier && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Detailed Usage Remaining Breakdown
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  (Based on {getCustomerUsageTierName(currentCustomer)})
                </Typography>
              </Typography>

              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Calls
              </Typography>
              <Grid container spacing={2}>
                {callUsageData.map((item, index) => (
                  <Grid item xs={12} sm={6} md={3} key={`call-${index}`}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {item.category} Calls
                      </Typography>
                      <Typography variant="body1">
                        {getDisplayValue(
                          item.remaining,
                          item.replenishment,
                          item.tierType,
                          "call"
                        )}
                      </Typography>
                      {shouldShowPercentage(
                        item.remaining,
                        item.replenishment,
                        item.tierType,
                        "call"
                      ) && (
                        <>
                          <LinearProgress
                            variant="determinate"
                            value={getUsagePercentage(
                              item.remaining,
                              item.replenishment
                            )}
                            sx={{
                              mt: 1,
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: colors[index],
                              },
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {getUsagePercentage(
                              item.remaining,
                              item.replenishment
                            ).toFixed(1)}
                            % used
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Tokens
              </Typography>
              <Grid container spacing={2}>
                {tokenUsageData.map((item, index) => (
                  <Grid item xs={12} sm={6} md={3} key={`token-${index}`}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {item.category} Tokens
                      </Typography>
                      <Typography variant="body1">
                        {getDisplayValue(
                          item.remaining,
                          item.replenishment,
                          item.tierType,
                          "token"
                        )}
                      </Typography>
                      {shouldShowPercentage(
                        item.remaining,
                        item.replenishment,
                        item.tierType,
                        "token"
                      ) && (
                        <>
                          <LinearProgress
                            variant="determinate"
                            value={getUsagePercentage(
                              item.remaining,
                              item.replenishment
                            )}
                            sx={{
                              mt: 1,
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: colors[index],
                              },
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {getUsagePercentage(
                              item.remaining,
                              item.replenishment
                            ).toFixed(1)}
                            % used
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Replenishment Amounts - Only show when customer has a usage tier */}
        {currentCustomer.usageTier && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Replenishment Information & Amounts
              </Typography>

              {/* Replenishment Settings */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Period
                  </Typography>
                  <Chip
                    label={
                      getCustomerReplenishmentPeriod(currentCustomer) ||
                      "Not set"
                    }
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Method
                  </Typography>
                  <Chip
                    label={
                      getCustomerReplenishmentMethod(currentCustomer) ||
                      "Not set"
                    }
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Next Replenishment
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {getNextReplenishmentDateFormatted()}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      height: "100%",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleReplenishNow}
                      disabled={isReplenishing}
                      sx={{ mb: 0.5 }}
                    >
                      {isReplenishing ? "Replenishing..." : "Replenish Now"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Replenishment Amounts
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  (From {getCustomerUsageTierName(currentCustomer)})
                </Typography>
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Call Replenishment
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Standard:
                      </Typography>
                      <Typography variant="body1">
                        {isUnlimited("standard", "call")
                          ? "Unlimited"
                          : formatNumber(
                              effectiveAmounts.standardCallReplenishmentAmount
                            )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Premium:
                      </Typography>
                      <Typography variant="body1">
                        {isUnlimited("premium", "call")
                          ? "Unlimited"
                          : formatNumber(
                              effectiveAmounts.premiumCallReplenishmentAmount
                            )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Super Premium:
                      </Typography>
                      <Typography variant="body1">
                        {isUnlimited("superPremium", "call")
                          ? "Unlimited"
                          : formatNumber(
                              effectiveAmounts.superPremiumCallReplenishmentAmount
                            )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Reasoning:
                      </Typography>
                      <Typography variant="body1">
                        {isUnlimited("reasoning", "call")
                          ? "Unlimited"
                          : formatNumber(
                              effectiveAmounts.reasoningCallReplenishmentAmount
                            )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Token Replenishment
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Standard:
                      </Typography>
                      <Typography variant="body1">
                        {isUnlimited("standard", "token")
                          ? "Unlimited"
                          : formatNumber(
                              effectiveAmounts.standardTokenReplenishmentAmount
                            )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Premium:
                      </Typography>
                      <Typography variant="body1">
                        {isUnlimited("premium", "token")
                          ? "Unlimited"
                          : formatNumber(
                              effectiveAmounts.premiumTokenReplenishmentAmount
                            )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Super Premium:
                      </Typography>
                      <Typography variant="body1">
                        {isUnlimited("superPremium", "token")
                          ? "Unlimited"
                          : formatNumber(
                              effectiveAmounts.superPremiumTokenReplenishmentAmount
                            )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Reasoning:
                      </Typography>
                      <Typography variant="body1">
                        {isUnlimited("reasoning", "token")
                          ? "Unlimited"
                          : formatNumber(
                              effectiveAmounts.reasoningTokenReplenishmentAmount
                            )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Message for customers without usage tiers */}
        {!currentCustomer.usageTier && (
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "info.light",
                color: "info.contrastText",
              }}
            >
              <Typography variant="h6" gutterBottom>
                No Usage Tier Assigned
              </Typography>
              <Typography variant="body1">
                This customer has no usage tier assigned, which means all
                replenishment amounts are 0. Assign a usage tier to enable usage
                limits and replenishment.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
